import { type FC } from 'react'
import classnames from 'classnames'
import { observer } from 'mobx-react-lite'
import { Badge, IconButton, Typography } from 'shared/ui'
import { NAME_INBOX } from 'entities/Inbox'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type Inbox } from 'entities/Inbox/model/Inbox'
import { GroupInbox } from 'entities/Inbox/model/GroupInbox'
import {
  DropdownSimpleInbox,
  DropdownGroupSimpleInbox,
} from 'pages/conversations/ui/ConversationMenu/ui/ConversationMenuInboxes/Dropdown'
import { useConversationMenuContext } from 'pages/conversations/ui/ConversationMenu/context'
import styles from './styles.module.scss'

type IMenuCardInboxProps = {
  item: Inbox
  groupInbox?: GroupInbox
  isCreate?: boolean
  onClickItemTree?: () => void
}

export const MenuCardInbox: FC<IMenuCardInboxProps> = observer(
  ({ item: inbox, groupInbox, isCreate, onClickItemTree }) => {
    const store = useConversationMenuContext()
    const { inboxesStore } = useEntitiesContext()
    const {
      handleUpdateTeamInbox,
      loadingUpdateTeam,
      deleteItemTree,
      disabledActive,
      resetCurrentConversation,
      config,
      isActiveConversationSearch,
    } = store
    const { selectCurrentInboxId } = inboxesStore
    const isGroup = groupInbox instanceof GroupInbox
    const active = selectCurrentInboxId === inbox.id
    const handleClickUpdateTeam = async () => {
      if (isCreate) return
      if (loadingUpdateTeam) return
      if (active && !isActiveConversationSearch) {
        onClickItemTree?.()

        return
      }

      inboxesStore.localInboxes.resetCurrentInbox()
      deleteItemTree(NAME_INBOX.Create)
      config?.resetConversations?.()
      config?.setConversationsLoading?.(true)
      resetCurrentConversation()
      await handleUpdateTeamInbox(inbox.id)
      onClickItemTree?.()
    }

    return (
      <div
        className={classnames(styles.wrap, {
          [styles.active]: active && !disabledActive,
          [styles.disabled]: loadingUpdateTeam,
        })}
        onClick={handleClickUpdateTeam}
      >
        <div
          className={classnames(styles.boxName, {
            [styles.muted]: inbox.isMuted,
            [styles.unread]: inbox.unreadCount,
          })}
        >
          <div className={styles.icon}>
            <IconButton
              iconProps={{
                color: !active && inbox.isMuted ? 'var(--content-neutral-primary)' : undefined,
              }}
              icon={inbox.icon}
              size={'small'}
              color={'transparent'}
              fontSize={16}
              ariaLabel='MenuCardInbox'
            />
          </div>

          <Typography
            variant={inbox.unreadCount && !inbox.isMuted ? 'body-md-semibold' : 'body-md-regular'}
            ellipsis
            color={
              inbox.isMuted ? 'var(--content-primary-tertiary)' : 'var(--content-primary-primary)'
            }
            ariaLabel={'name'}
          >
            {inbox.name}
          </Typography>
        </div>

        {Boolean(inbox.unreadCount) && (
          <div className={styles.right}>
            <div
              className={classnames(styles.boxInfo, {
                [styles.isMuted]: inbox.isMuted,
                [styles.unread]: inbox.unreadCount,
              })}
            >
              <div className={styles.infoItem}>
                <Badge
                  content={inbox.unreadCount}
                  ariaLabel={'MenuCardInbox_unreadMessagesCount'}
                />
              </div>
            </div>
          </div>
        )}
        <div className={styles.boxDropdownMenu}>
          {!isCreate && (
            <div className={styles.dropdownMenu}>
              {!isGroup && <DropdownSimpleInbox inbox={inbox} />}
              {isGroup && <DropdownGroupSimpleInbox inbox={inbox} groupInbox={groupInbox} />}
            </div>
          )}
        </div>
      </div>
    )
  }
)
